// components/InvoiceRequest/InvoiceRequestDeleteDialog.tsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Box
} from '@mui/material';
import { invoiceRequestService } from '../../services/invoiceRequest';
import { useUser } from '../../hooks/useUser';

interface InvoiceRequestDeleteDialogProps {
    open: boolean;
    onClose: () => void;
    invoiceRequestId?: number;
    invoiceRequestInfo?: {
        customerName?: string;
        siteName?: string;
        billingMonth?: string;
    };
    onSuccess: () => void;
}

export const InvoiceRequestDeleteDialog: React.FC<InvoiceRequestDeleteDialogProps> = ({
    open,
    onClose,
    invoiceRequestId,
    invoiceRequestInfo,
    onSuccess
}) => {
    const { userInfo } = useUser();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleDelete = async () => {
        if (!invoiceRequestId) return;

        setLoading(true);
        setError('');

        try {
            // ログインユーザーの情報を取得（実際のアプリの認証状態から取得する必要があります）
            const updatedByName = userInfo?.lastName || "削除者";

            await invoiceRequestService.delete(invoiceRequestId, {
                updatedByName
            });

            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('請求書作成依頼の削除に失敗しました:', err);
            setError('請求書作成依頼の削除に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            return `${date.getFullYear()}年${date.getMonth() + 1}月`;
        } catch {
            return dateString;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableRestoreFocus>
            <DialogTitle sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                px: 3,
                py: 2,
                color: 'error.main'
            }}>
                請求書作成依頼の削除
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 3 }}>
                <Typography variant="body1" gutterBottom>
                    以下の請求書作成依頼を削除してもよろしいですか？
                </Typography>

                {invoiceRequestInfo && (
                    <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        {invoiceRequestInfo.customerName && (
                            <Typography variant="body2" gutterBottom>
                                <strong>お客様名:</strong> {invoiceRequestInfo.customerName}
                            </Typography>
                        )}

                        {invoiceRequestInfo.siteName && (
                            <Typography variant="body2" gutterBottom>
                                <strong>現場名:</strong> {invoiceRequestInfo.siteName}
                            </Typography>
                        )}

                        {invoiceRequestInfo.billingMonth && (
                            <Typography variant="body2">
                                <strong>請求月:</strong> {formatDate(invoiceRequestInfo.billingMonth)}
                            </Typography>
                        )}
                    </Box>
                )}

                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    ※この操作は取り消せません。
                </Typography>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{
                borderTop: 1,
                borderColor: 'divider',
                px: 3,
                py: 2
            }}>
                <Button
                    onClick={handleClose}
                    color="primary"
                    disabled={loading}
                    variant="outlined"
                >
                    キャンセル
                </Button>
                <Button
                    onClick={handleDelete}
                    color="error"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                    {loading ? '削除中...' : '削除する'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};